import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, API_BASE } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Download, Bookmark, Utensils, ExternalLink, PlayCircle } from 'lucide-react';
import DietPlanView from '@/components/diet/DietPlanView';

const Dashboard = () => {
  const { user, checkAuth } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rx, setRx] = useState([]);
  const [saved, setSaved] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [profile, setProfile] = useState({ name: '', phone: '', date_of_birth: '', gender: '' });
  const [dietPlans, setDietPlans] = useState([]);
  const [dietStatus, setDietStatus] = useState(null);

  const loadAll = () => {
    api.get('/assessment').then(({ data }) => setAssessments(data)).catch(() => {});
    api.get('/consultations/mine').then(({ data }) => setAppointments(data)).catch(() => {});
    api.get('/shop/orders').then(({ data }) => setOrders(data)).catch(() => {});
    api.get('/me/prescriptions').then(({ data }) => setRx(data)).catch(() => {});
    api.get('/me/saved-articles').then(({ data }) => setSaved(data)).catch(() => {});
    api.get('/me/notifications').then(({ data }) => setNotifs(data)).catch(() => {});
    api.get('/me/profile').then(({ data }) => setProfile({ name: data.name || '', phone: data.phone || '', date_of_birth: data.date_of_birth || '', gender: data.gender || '' })).catch(() => {});
    api.get('/diet/mine').then(({ data }) => setDietPlans(data)).catch(() => {});
    api.get('/diet/mine/status').then(({ data }) => setDietStatus(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const markAllRead = async () => {
    try { await api.post('/me/notifications/read-all'); setNotifs((n) => n.map((x) => ({ ...x, read: true }))); }
    catch { toast.error('Failed'); }
  };

  const saveProfile = async () => {
    try { await api.put('/me/profile', profile); toast.success('Profile updated'); await checkAuth(); }
    catch { toast.error('Save failed'); }
  };

  const unsave = async (slug) => {
    try { await api.delete(`/me/saved-articles/${slug}`); setSaved((s) => s.filter((a) => a.slug !== slug)); }
    catch { toast.error('Failed'); }
  };

  const InputCls = 'w-full border-b border-sans_line bg-transparent px-0 py-3 text-base text-sans_ink focus:outline-none focus:border-sans_ink transition-colors';

  return (
    <div className="bg-sans_bone">
      <section className="sans-section">
        <div className="sans-container">
          <div className="sans-label mb-6">Your dashboard</div>
          <h1 className="sans-h1">Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.</h1>
          <p className="sans-body mt-6 max-w-xl">Your assessments, consultations, prescriptions, orders — all in one place.</p>

          <div className="mt-14 grid sm:grid-cols-3 gap-4">
            <Link to="/assessment" className="border border-sans_line bg-white p-8 hover:shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-shadow">
              <div className="sans-label mb-3">Free</div>
              <div className="font-serif text-2xl text-sans_ink">Take Prakriti Assessment</div>
            </Link>
            <Link to="/consultation" className="border border-sans_line bg-white p-8 hover:shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-shadow">
              <div className="sans-label mb-3">₹1000</div>
              <div className="font-serif text-2xl text-sans_ink">Book Consultation</div>
            </Link>
            <Link to="/products" className="border border-sans_line bg-white p-8 hover:shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-shadow">
              <div className="sans-label mb-3">Shop</div>
              <div className="font-serif text-2xl text-sans_ink">Explore products</div>
            </Link>
          </div>

          <div className="mt-16">
            <Tabs defaultValue="assessments" className="w-full">
              <TabsList className="bg-transparent border-b border-sans_line w-full justify-start gap-6 md:gap-8 p-0 h-auto rounded-none overflow-x-auto no-scrollbar">
                {[
                  ['assessments', 'Assessments'],
                  ['appointments', 'Consultations'],
                  ['diet', dietStatus?.has_appointment ? 'My Diet Chart' : ''],
                  ['prescriptions', 'Prescriptions'],
                  ['orders', 'Orders'],
                  ['saved', 'Saved articles'],
                  ['notifications', `Notifications${unread ? ` · ${unread}` : ''}`],
                  ['profile', 'Profile'],
                ].filter(([, l]) => l).map(([v, l]) => (
                  <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-none border-b-2 border-transparent data-[state=active]:border-sans_ink data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-4 text-sans_mute data-[state=active]:text-sans_ink whitespace-nowrap">{l}</TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="assessments" className="pt-10">
                {assessments.length === 0 && <div className="text-sans_mute text-sm">No assessments yet. <Link to="/assessment" className="sans-link">Take the free assessment</Link>.</div>}
                <div className="space-y-3">
                  {assessments.map((a) => (
                    <Link key={a.assessment_id} to={`/assessment/${a.assessment_id}`} className="block border border-sans_line bg-white p-6 hover:shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-shadow">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="sans-label mb-2">{new Date(a.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</div>
                          <div className="font-serif text-xl text-sans_ink">{a.report ? `${a.report.primary_dosha} / ${a.report.secondary_dosha}` : 'In progress'}</div>
                        </div>
                        <div className="text-xs uppercase tracking-[0.2em] text-sans_mute">{a.status}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="appointments" className="pt-10">
                {appointments.length === 0 && <div className="text-sans_mute text-sm">No consultations yet. <Link to="/consultation" className="sans-link">Book one</Link>.</div>}
                <div className="space-y-3">
                  {appointments.map((a) => (
                    <div key={a.appointment_id} className="border border-sans_line bg-white p-6">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <div className="sans-label mb-2">{a.status}{a.type === 'diet' ? ' · Diet Consultation' : ''}</div>
                          <div className="font-serif text-xl text-sans_ink">{a.doctor_name}</div>
                          <div className="text-sm text-sans_mute mt-1">{new Date(a.slot_datetime).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' })}</div>
                        </div>
                        {a.status === 'confirmed' && <a href={a.meet_link} target="_blank" rel="noreferrer" className="sans-btn-secondary">Join meeting</a>}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="diet" className="pt-10">
                {dietStatus?.has_appointment && (
                  <div className="mb-8">
                    <div className="sans-label mb-4">Your diet plan progress</div>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        ['consultation_booked', 'Consultation Booked'],
                        ['preparing', 'Being Prepared'],
                        ['ready', 'Ready'],
                        ['completed', 'Completed'],
                      ].map(([k, label], i, arr) => {
                        const order = arr.map(([kk]) => kk);
                        const currentIdx = order.indexOf(dietStatus.status);
                        const done = i <= currentIdx;
                        return (
                          <div key={k} className={`border p-4 ${done ? 'border-sans_ink bg-sans_ink text-sans_bone' : 'border-sans_line bg-white text-sans_mute'}`}>
                            <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">Step {i + 1}</div>
                            <div className="font-serif text-sm mt-1 leading-snug">{label}</div>
                          </div>
                        );
                      })}
                    </div>
                    {dietStatus.status === 'consultation_booked' && (
                      <p className="mt-4 text-sm text-sans_mute italic">Your consultation is booked. After it, your specialist will personally prepare your diet plan.</p>
                    )}
                    {dietStatus.status === 'preparing' && (
                      <p className="mt-4 text-sm text-sans_mute italic">Your specialist is preparing your personalised diet — usually within 3-5 days of your consultation.</p>
                    )}
                  </div>
                )}
                {!dietStatus?.has_appointment && (
                  <div className="border border-sans_line bg-white p-10 text-center">
                    <Utensils className="w-6 h-6 mx-auto text-sans_mute" strokeWidth={1.5} />
                    <div className="font-serif text-xl text-sans_ink mt-4">No diet consultation yet</div>
                    <p className="text-sans_mute text-sm mt-2 max-w-md mx-auto">Book a ₹2,900 personalised diet consultation with our specialist. Your plan is manually prepared — never AI-generated.</p>
                    <Link to="/consultation/diet" className="sans-btn-primary mt-6 inline-block">Book diet consultation</Link>
                  </div>
                )}
                {dietPlans.length > 0 && (
                  <div className="mt-4">
                    <DietPlanView plan={dietPlans[0]} />
                  </div>
                )}
              </TabsContent>

              <TabsContent value="prescriptions" className="pt-10">
                {rx.length === 0 && <div className="text-sans_mute text-sm">No prescriptions yet. Prescriptions issued by your doctor appear here.</div>}
                <div className="space-y-3">
                  {rx.map((r) => (
                    <div key={r.prescription_id} className="border border-sans_line bg-white p-6">
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <div className="sans-label mb-2">{new Date(r.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</div>
                          <div className="font-serif text-xl text-sans_ink">Dr. {r.doctor_name}</div>
                          <div className="text-sm text-sans_mute mt-1">{r.items?.length} formulation{r.items?.length !== 1 ? 's' : ''}{r.follow_up_date ? ` · follow-up ${r.follow_up_date}` : ''}</div>
                        </div>
                        <a href={`${API_BASE}/me/prescriptions/${r.prescription_id}/pdf?_t=${localStorage.getItem('sans_token') || ''}`}
                           target="_blank" rel="noreferrer"
                           onClick={async (e) => {
                             e.preventDefault();
                             const res = await api.get(`/me/prescriptions/${r.prescription_id}/pdf`, { responseType: 'blob' });
                             const url = URL.createObjectURL(res.data);
                             window.open(url, '_blank');
                           }}
                           data-testid={`rx-download-${r.prescription_id}`}
                           className="sans-btn-secondary text-xs">
                          <Download className="w-4 h-4 mr-1" strokeWidth={1.5} /> Download PDF
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="orders" className="pt-10">
                {orders.length === 0 && <div className="text-sans_mute text-sm">No orders yet. <Link to="/products" className="sans-link">Browse products</Link>.</div>}
                <div className="space-y-3">
                  {orders.map((o) => (
                    <div key={o.order_id} className="border border-sans_line bg-white p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="sans-label mb-2">{new Date(o.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</div>
                          <div className="font-serif text-xl text-sans_ink">Order {o.order_id}</div>
                          <div className="text-sm text-sans_mute mt-1">{o.items.length} item{o.items.length !== 1 ? 's' : ''} · ₹{o.total}</div>
                        </div>
                        <div className="text-xs uppercase tracking-[0.2em] text-sans_mute">{o.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="saved" className="pt-10">
                {saved.length === 0 && <div className="text-sans_mute text-sm">No saved articles yet. <Link to="/learn" className="sans-link">Browse the Knowledge Hub</Link>.</div>}
                <div className="grid md:grid-cols-2 gap-4">
                  {saved.map((a) => (
                    <div key={a.slug} className="border border-sans_line bg-white p-6">
                      <div className="flex justify-between items-start gap-4">
                        <Link to={`/learn/${a.slug}`} className="block">
                          <div className="sans-label mb-2">{a.category} · {a.reading_time} min</div>
                          <div className="font-serif text-xl text-sans_ink hover:opacity-70">{a.title}</div>
                          {a.excerpt && <p className="text-sm text-sans_mute mt-2 line-clamp-2">{a.excerpt}</p>}
                        </Link>
                        <button onClick={() => unsave(a.slug)} className="text-sans_mute hover:text-red-600 text-xs" aria-label="Unsave">Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="notifications" className="pt-10">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-sm text-sans_mute">{unread} unread</div>
                  {unread > 0 && <button onClick={markAllRead} className="text-xs text-sans_ink underline">Mark all read</button>}
                </div>
                {notifs.length === 0 && <div className="text-sans_mute text-sm">You're all caught up.</div>}
                <div className="space-y-3">
                  {notifs.map((n) => (
                    <div key={n.notification_id} className={`border border-sans_line p-5 ${n.read ? 'bg-white' : 'bg-white border-l-2 border-l-sans_ink'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-serif text-lg text-sans_ink">{n.title}</div>
                          <div className="text-sm text-sans_mute mt-1">{n.body}</div>
                          {n.link && <Link to={n.link} className="text-xs text-sans_ink border-b border-sans_ink pb-0.5 mt-3 inline-block">View →</Link>}
                        </div>
                        <div className="text-xs text-sans_mute">{new Date(n.created_at).toLocaleDateString('en-IN')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="profile" className="pt-10">
                <div className="max-w-xl grid grid-cols-2 gap-6">
                  <div className="col-span-2"><label className="sans-label block mb-2">Name</label><input className={InputCls} value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
                  <div><label className="sans-label block mb-2">Phone</label><input className={InputCls} value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
                  <div><label className="sans-label block mb-2">Date of birth</label><input type="date" className={InputCls} value={profile.date_of_birth} onChange={(e) => setProfile({ ...profile, date_of_birth: e.target.value })} /></div>
                  <div><label className="sans-label block mb-2">Gender</label>
                    <select value={profile.gender} onChange={(e) => setProfile({ ...profile, gender: e.target.value })} className={InputCls}>
                      <option value="">Prefer not to say</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="col-span-2 mt-4">
                    <button onClick={saveProfile} data-testid="profile-save" className="sans-btn-primary">Save profile</button>
                  </div>
                </div>
                <div className="mt-10 text-xs text-sans_mute">Email: {user?.email} · Role: {user?.role}</div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
